import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays, MapPin, Clock, Package, ShoppingBasket, NotebookPen, Plus, Pencil, Trash2,
  X, DollarSign, ClipboardList, ArrowRight, TrendingUp, BarChart3, Receipt,
} from "lucide-react";
import {
  getEvents, saveEvent, deleteEvent, formatCurrency, getEventSummary,
  FREE_EVENT_LIMIT,
} from "../lib/storage";
import { useProState } from "../lib/pro";
import ProBadge from "../components/ProBadge";
import UpgradeButton from "../components/UpgradeButton";
import EventProductsSection from "../components/EventProductsSection";
import { toast } from "sonner";

const emptyEvent = {
  id: null,
  name: "",
  date: "",
  location: "",
  // Costs
  boothFee: "",
  travelCost: "",
  inventoryCost: "",
  suppliesCost: "",
  foodCost: "",
  marketingCost: "",
  otherCost: "",
  // Sales estimate
  expectedSales: "",
  avgSale: "",
  // Logistics
  setupTime: "",
  startTime: "",
  endTime: "",
  notes: "",
  products: "",
  itemsToBring: "",
  // Product Library assignments (Pro)
  eventProducts: [],
};

const verdictTone = {
  good: "bg-brand-success/10 text-brand-success border-brand-success/30",
  risky: "bg-brand-warning/15 text-brand-warning border-brand-warning/30",
  bad: "bg-brand-error/10 text-brand-error border-brand-error/30",
  neutral: "bg-brand-cream-soft text-brand-mute border-brand-line",
};

export default function Planner() {
  const { pro, openUpgrade } = useProState();
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);

  const refresh = () => setEvents(getEvents().sort((a, b) => (b.date || "").localeCompare(a.date || "")));
  useEffect(() => { refresh(); }, []);

  const canAdd = () => pro || events.length < FREE_EVENT_LIMIT;

  const onAddClick = () => {
    if (!canAdd()) {
      openUpgrade();
      return;
    }
    setEditing({ ...emptyEvent });
  };

  const onSave = (data) => {
    if (!data.name?.trim()) { toast.error("Please enter an event name"); return; }
    // For new events, enforce limit one more time
    if (!data.id && !canAdd()) { openUpgrade(); return; }
    saveEvent(data);
    toast.success(data.id ? "Event updated" : "Event saved");
    setEditing(null);
    refresh();
  };

  const onDelete = (id) => {
    const ev = events.find((e) => e.id === id);
    const name = ev?.name?.trim() || "this event";
    toast(`Delete "${name}"?`, {
      description: "This can't be undone.",
      duration: 6000,
      action: {
        label: "Delete",
        onClick: () => {
          deleteEvent(id);
          toast.success("Event deleted");
          refresh();
        },
      },
    });
  };

  const free = !pro;
  const atLimit = free && events.length >= FREE_EVENT_LIMIT;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 pb-24 md:pb-16" data-testid="planner-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="label-eyebrow">Event Planner</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink mt-2">
            Your upcoming events
          </h1>
          <p className="text-brand-mute text-sm mt-1.5">Plan, save, and revisit every market — booth costs, schedule, and outcomes.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/compare" className="pop-btn-secondary" data-testid="planner-compare-link">
            <BarChart3 className="w-4 h-4" /> Compare <ProBadge className="ml-1" />
          </Link>
          <button
            onClick={onAddClick}
            className="pop-btn-primary"
            data-testid="planner-add-event-btn"
          >
            <Plus className="w-4 h-4" /> Add event
          </button>
        </div>
      </div>

      {/* Free plan banner */}
      {free && (
        <div className="pop-card p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="planner-plan-banner">
          <span className="pop-pill bg-brand-green/10 text-brand-green border border-brand-green/20">
            Free plan
          </span>
          <p className="text-sm text-brand-ink flex-1 min-w-[180px]">
            <span className="font-semibold">{events.length} of {FREE_EVENT_LIMIT}</span>{" "}
            active event{FREE_EVENT_LIMIT === 1 ? "" : "s"} used.{" "}
            <span className="text-brand-mute">Unlock unlimited events with Pro.</span>
          </p>
          <UpgradeButton variant="ghost" label="Upgrade — $9/month" testid="planner-upgrade-btn" />
        </div>
      )}

      {events.length === 0 ? (
        <EmptyState onCreate={onAddClick} />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((ev) => (
            <EventCard key={ev.id} event={ev} onEdit={() => setEditing(ev)} onDelete={() => onDelete(ev.id)} />
          ))}
        </div>
      )}

      {atLimit && events.length > 0 && (
        <div className="mt-8 pop-card p-6 text-center bg-brand-gold/5 border-brand-gold/30" data-testid="planner-at-limit">
          <p className="font-heading text-lg font-semibold text-brand-ink">You've hit your free event limit.</p>
          <p className="text-sm text-brand-mute mt-1.5 max-w-md mx-auto">
            Pro unlocks unlimited saved events, full history, comparison, and post-event reports.
          </p>
          <div className="mt-4">
            <UpgradeButton variant="primary" label="Upgrade to Pro — $9/month" testid="planner-limit-upgrade-btn" />
          </div>
        </div>
      )}

      {editing && (
        <EventForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="pop-card p-10 text-center" data-testid="planner-empty-state">
      <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-brand-green/10 text-brand-green">
        <CalendarDays className="w-7 h-7" strokeWidth={2} />
      </span>
      <h3 className="font-heading text-xl font-semibold text-brand-ink mt-4">No events yet</h3>
      <p className="text-brand-mute text-sm mt-1.5 max-w-md mx-auto">
        Add your first market, craft fair, or vendor event to start planning.
      </p>
      <button onClick={onCreate} className="pop-btn-primary mt-5" data-testid="planner-empty-add-btn">
        <Plus className="w-4 h-4" /> Add your first event
      </button>
    </div>
  );
}

function EventCard({ event, onEdit, onDelete }) {
  const summary = getEventSummary(event);
  const dateLabel = event.date
    ? new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
    : "Date TBD";
  const repeat = summary.repeat;

  return (
    <article className="pop-card pop-card-hover p-5 flex flex-col" data-testid={`event-card-${event.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-lg font-semibold text-brand-ink truncate">{event.name}</h3>
          <p className="text-sm text-brand-mute mt-1 flex items-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5" strokeWidth={2} /> {dateLabel}
          </p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button onClick={onEdit} className="w-9 h-9 grid place-items-center rounded-full hover:bg-brand-cream-soft text-brand-mute hover:text-brand-green transition-colors" data-testid={`event-edit-${event.id}`} aria-label="Edit">
            <Pencil className="w-4 h-4" />
          </button>
          <button onClick={onDelete} className="w-9 h-9 grid place-items-center rounded-full hover:bg-brand-error/10 text-brand-mute hover:text-brand-error transition-colors" data-testid={`event-delete-${event.id}`} aria-label="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mt-3 space-y-1.5 text-sm text-brand-mute">
        {event.location && (
          <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" strokeWidth={2} /> {event.location}</p>
        )}
      </div>

      {/* Profit row */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-brand-cream-soft p-3">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-mute">Estimated profit</p>
          <p className="font-heading text-lg font-semibold text-brand-ink mt-1 tabular-nums">
            {formatCurrency(summary.estimate.profit)}
          </p>
        </div>
        <div className={`rounded-xl p-3 border ${summary.actual ? "bg-brand-green/5 border-brand-green/20" : "bg-brand-cream-soft border-transparent"}`}>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-mute">Actual profit</p>
          <p className={`font-heading text-lg font-semibold mt-1 tabular-nums ${summary.actual ? "text-brand-green" : "text-brand-mute/70"}`}>
            {summary.actual ? formatCurrency(summary.actual.profit) : "—"}
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <span className={`pop-pill border ${verdictTone[repeat.tone]}`} data-testid={`event-verdict-${event.id}`}>
          {repeat.label}
        </span>
        {!event.report?.completed && (
          <span className="pop-pill bg-brand-cream-soft text-brand-mute border border-brand-line">
            Report pending
          </span>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-brand-line flex items-center justify-between gap-3">
        <Link to={`/event/${event.id}`} className="text-sm font-medium text-brand-green hover:underline inline-flex items-center gap-1" data-testid={`event-open-${event.id}`}>
          <ClipboardList className="w-3.5 h-3.5" />
          {event.report?.completed ? "View report" : "Add post-event report"}
        </Link>
        <Link to={`/event/${event.id}`} className="text-brand-mute hover:text-brand-green transition-colors">
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </article>
  );
}

function EventForm({ initial, onClose, onSave }) {
  const { pro } = useProState();
  const [form, setForm] = useState({ ...emptyEvent, ...initial, eventProducts: initial.eventProducts || [] });
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const setEventProducts = (eventProducts) => setForm((s) => ({ ...s, eventProducts }));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-brand-ink/40 backdrop-blur-sm p-0 sm:p-4 animate-fade-up" data-testid="event-form-modal">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl max-h-[92dvh] overflow-y-auto shadow-2xl border border-brand-line">
        <div className="sticky top-0 bg-white/95 backdrop-blur z-10 flex items-center justify-between p-5 border-b border-brand-line">
          <div>
            <p className="label-eyebrow">{form.id ? "Edit event" : "New event"}</p>
            <h3 className="font-heading text-xl font-semibold text-brand-ink mt-1">{form.name || "Untitled event"}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-brand-cream-soft text-brand-mute" data-testid="event-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-7">
          {/* Basics */}
          <FormSection title="Basics">
            <Field label="Event name" icon={NotebookPen}>
              <input className="pop-input" value={form.name} onChange={set("name")} placeholder="Saturday Farmers Market" data-testid="event-input-name" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Event date" icon={CalendarDays}>
                <input type="date" className="pop-input" value={form.date} onChange={set("date")} data-testid="event-input-date" />
              </Field>
              <Field label="Location" icon={MapPin}>
                <input className="pop-input" value={form.location} onChange={set("location")} placeholder="City park, Main St." data-testid="event-input-location" />
              </Field>
            </div>
          </FormSection>

          {/* Estimate — Costs */}
          <FormSection title="Estimated costs" subtitle="Anything you'll spend to show up." icon={Receipt}>
            <div className="grid sm:grid-cols-2 gap-4">
              <CurrencyField id="event-input-boothfee" label="Booth fee" value={form.boothFee} onChange={set("boothFee")} />
              <CurrencyField id="event-input-travel" label="Travel cost" value={form.travelCost} onChange={set("travelCost")} />
              <CurrencyField id="event-input-inventory" label="Inventory cost" value={form.inventoryCost} onChange={set("inventoryCost")} />
              <CurrencyField id="event-input-supplies" label="Supplies cost" value={form.suppliesCost} onChange={set("suppliesCost")} />
              <CurrencyField id="event-input-food" label="Food / drink" value={form.foodCost} onChange={set("foodCost")} />
              <CurrencyField id="event-input-marketing" label="Marketing / signage" value={form.marketingCost} onChange={set("marketingCost")} />
              <CurrencyField id="event-input-other" label="Other costs" value={form.otherCost} onChange={set("otherCost")} />
            </div>
          </FormSection>

          {/* Estimate — Sales */}
          <FormSection title="Estimated sales" subtitle="Your honest best guess works fine." icon={TrendingUp}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Expected number of sales">
                <input type="number" min="0" inputMode="numeric" className="pop-input" value={form.expectedSales} onChange={set("expectedSales")} placeholder="0" data-testid="event-input-expectedSales" />
              </Field>
              <CurrencyField id="event-input-avgSale" label="Average sale amount" value={form.avgSale} onChange={set("avgSale")} />
            </div>
          </FormSection>

          {/* Logistics */}
          <FormSection title="Schedule">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Setup time">
                <input type="time" className="pop-input" value={form.setupTime} onChange={set("setupTime")} data-testid="event-input-setup" />
              </Field>
              <Field label="Start time">
                <input type="time" className="pop-input" value={form.startTime} onChange={set("startTime")} data-testid="event-input-start" />
              </Field>
              <Field label="End time">
                <input type="time" className="pop-input" value={form.endTime} onChange={set("endTime")} data-testid="event-input-end" />
              </Field>
            </div>
          </FormSection>

          {/* Items */}
          <FormSection title="Products & items">
            <Field label="Products / items being sold" icon={Package}>
              <textarea rows="3" className="pop-input resize-none" value={form.products} onChange={set("products")} placeholder="Candles, soap bars, lavender sachets…" data-testid="event-input-products" />
            </Field>

            {/* Product Library — Pro feature, grouped next to the products-being-sold field */}
            <EventProductsSection
              value={form.eventProducts}
              onChange={setEventProducts}
              pro={pro}
            />

            <Field label="Items to bring" icon={ShoppingBasket}>
              <textarea rows="3" className="pop-input resize-none" value={form.itemsToBring} onChange={set("itemsToBring")} placeholder="Tablecloth, square reader, extra inventory…" data-testid="event-input-items" />
            </Field>
            <Field label="Notes">
              <textarea rows="2" className="pop-input resize-none" value={form.notes} onChange={set("notes")} placeholder="Vendor parking on west side…" data-testid="event-input-notes" />
            </Field>
          </FormSection>
        </div>

        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-brand-line p-4 max-sm:pb-24 flex gap-3 justify-end">
          <button onClick={onClose} className="pop-btn-secondary" data-testid="event-form-cancel">Cancel</button>
          <button onClick={() => onSave(form)} className="pop-btn-primary" data-testid="event-form-save">
            {form.id ? "Save changes" : "Save event"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormSection({ title, subtitle, icon: Icon, children }) {
  return (
    <section>
      <div className="flex items-center gap-2.5 mb-3">
        {Icon && (
          <span className="w-8 h-8 rounded-lg bg-brand-green/10 text-brand-green grid place-items-center">
            <Icon className="w-4 h-4" strokeWidth={2} />
          </span>
        )}
        <div>
          <h4 className="font-heading text-sm font-semibold text-brand-ink uppercase tracking-wider">{title}</h4>
          {subtitle && <p className="text-xs text-brand-mute mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="pop-label flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-brand-mute" strokeWidth={2} />}
        {label}
      </label>
      {children}
    </div>
  );
}

function CurrencyField({ id, label, value, onChange }) {
  return (
    <div>
      <label className="pop-label" htmlFor={id}>{label}</label>
      <div className="relative">
        <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute pointer-events-none" />
        <input
          id={id}
          data-testid={id}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={value}
          onChange={onChange}
          className="pop-input pl-11"
        />
      </div>
    </div>
  );
}
